import React, { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import io, { Socket } from 'socket.io-client';
import { SOCKET_IO_PATH } from '../utils/socketConfig';
import { useAuth } from './AuthContext';

// Types pour les notifications
export interface Notification {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: Date;
  source?: string;
  userId?: string;
  metadata?: any;
}

interface NotificationState {
  notifications: Notification[];
  isConnected: boolean;
  adminCount: number;
}

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'SET_CONNECTION_STATUS'; payload: { isConnected: boolean; adminCount: number } };

// Contexte
interface NotificationContextType {
  state: NotificationState;
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Reducer pour gérer l'état des notifications
const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 50) // Limiter à 50 notifications
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      };
    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        isConnected: action.payload.isConnected,
        adminCount: action.payload.adminCount
      };
    default:
      return state;
  }
};

// Provider
interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: [],
    isConnected: false,
    adminCount: 0
  });

  const [socket, setSocket] = useState<Socket | null>(null);

  // Connexion WebSocket - DÉSACTIVÉ TEMPORAIREMENT
  useEffect(() => {
    // Désactivé pour réduire le bruit et les connexions inutiles
    // Pour réactiver : décommenter tout ce bloc
    /*
    if (user && user.role === 'admin') {
      // Extraire l'URL de base sans /api pour Socket.IO
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const baseUrl = apiUrl.replace('/api', '');
      
      console.log('🔌 Connecting to Socket.IO at:', baseUrl);
      
      const newSocket = io(baseUrl, {
        path: SOCKET_IO_PATH,
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      });

      newSocket.on('connect', () => {
        console.log('🔔 Connected to notification service');
        dispatch({
          type: 'SET_CONNECTION_STATUS',
          payload: { isConnected: true, adminCount: 0 }
        });

        // Rejoindre la room des admins
        newSocket.emit('admin-join', {
          email: user.email,
          role: user.role
        });
      });

      newSocket.on('disconnect', (reason) => {
        console.log('🔔 Disconnected from notification service:', reason);
        dispatch({
          type: 'SET_CONNECTION_STATUS',
          payload: { isConnected: false, adminCount: 0 }
        });
      });

      newSocket.on('connect_error', (error) => {
        console.error('🔔 Connection error:', error);
        dispatch({
          type: 'SET_CONNECTION_STATUS',
          payload: { isConnected: false, adminCount: 0 }
        });
      });

      newSocket.on('notification', (notification: Notification) => {
        console.log('🔔 Received notification:', notification);
        
        // Convertir la timestamp string en Date
        const notificationWithDate = {
          ...notification,
          timestamp: new Date(notification.timestamp)
        };
        
        dispatch({
          type: 'ADD_NOTIFICATION',
          payload: notificationWithDate
        });
      });

      newSocket.on('status', (status: { connected: boolean; adminCount: number }) => {
        dispatch({
          type: 'SET_CONNECTION_STATUS',
          payload: { isConnected: status.connected, adminCount: status.adminCount }
        });
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
        setSocket(null);
      };
    }
    */
  }, [user]);

  // Demander le statut périodiquement - DÉSACTIVÉ TEMPORAIREMENT
  useEffect(() => {
    // Désactivé car WebSocket est désactivé
    /*
    if (socket && state.isConnected) {
      const interval = setInterval(() => {
        socket.emit('get-status');
      }, 30000); // Toutes les 30 secondes

      return () => clearInterval(interval);
    }
    */
  }, [socket, state.isConnected]);

  const addNotification = (notification: Notification) => {
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: notification
    });
  };

  const removeNotification = (id: string) => {
    dispatch({
      type: 'REMOVE_NOTIFICATION',
      payload: id
    });
  };

  const clearNotifications = () => {
    dispatch({
      type: 'CLEAR_NOTIFICATIONS'
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        state,
        addNotification,
        removeNotification,
        clearNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Hook personnalisé
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
